import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Select, { MenuListProps } from "react-select";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/redux/store";
import { adjustedInventoryAdjustmentAsync, createInventoryAdjustmentAsync, deleteItemInventoryAdjustmentAsync, editInventoryAdjustmentAsync, getInventoryVariantsListAsync, getReasonListAsync, getWarehouseListAsync, updateInventoryAdjustmentAsync } from "src/redux/actions/omsPageActions";
import { toast } from "react-toastify";
import { formatDateForPayload, KTIcon } from "src/marketConnect/helpers";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import Loader from "src/utils/Loader";
import { components } from "react-select";
import ManageReasonModal from "./ReasonModal";
import {
  createInventoryItemSelectStyles,
  getInventoryItemMenuLayout,
  InventoryItemSelect,
} from "../inventory/inventoryItemSelectMenuProps";

registerLocale("en-GB", enGB);
interface ItemOption {
    label: string;
    value: string;
    quantity: number;
    unit: string;
    source_total_quantity?: number;
    sku?: string;
    product_id?: number;
    adjustment_item_id?: number;
}
interface TableItem {
    item: ItemOption | null;
    sourceAvailableQuantity: string;
    newQuantity: string;
    quantityAdjusted: string;
    searchTerm: string; // store search term per row
    page: number;
    hasMore: boolean;
    options: ItemOption[];
    loading: boolean;
    adjustment_item_id?: number;
}
interface EditData {
    mode: "QUANTITY_BASED_ADJUSTMENT" | "ONHAND_BASED_ADJUSTMENT";
    reference: string;
    date: string;
    description: string;
    location: { label: string; value: string } | null;
    items: TableItem[];
    id: number;
    sync_status: string;
}

interface Props {
    show: boolean;
    onClose: () => void;
    onSaved: (form: any) => void;
    editData?: EditData;
}

type ReasonOption = { label: string; value: number };

const CreateEditAdjustmentModal: React.FC<Props> = ({ show, onClose, onSaved, editData }) => {
    const dispatch = useDispatch<AppDispatch>();
    const searchTimers = useRef<Record<number, NodeJS.Timeout>>({});
    const itemSelectAnchorRefs = useRef<Array<HTMLDivElement | null>>([]);
    const [openItemSelectIndex, setOpenItemSelectIndex] = useState<number | null>(null);
    const isSynced = !!editData && editData?.sync_status === "SYNCED";
    const [form, setForm] = useState({
        mode: "QUANTITY_BASED_ADJUSTMENT" as "QUANTITY_BASED_ADJUSTMENT" | "ONHAND_BASED_ADJUSTMENT",
        reference: "",
        date: null as Date | null,
        description: "",
        location: null as { label: string; value: string } | null,
        items: [
            {
                item: null as ItemOption | null,
                sourceAvailableQuantity: "",
                newQuantity: "",
                quantityAdjusted: "",
                searchTerm: "",
                page: 1,
                hasMore: true,
                options: [],
                loading: false,
            },
        ] as TableItem[],
    });

    const [warehouseOptions, setWarehouseOptions] = useState<{ label: string; value: string }[]>([]);
    const isTotalQuantityAdjustment = form.mode === "ONHAND_BASED_ADJUSTMENT";
    const [locationSearchTerm, setLocationSearchTerm] = useState('');
    const [locationPage, setLocationPage] = useState(1);
    const [locationHasMore, setLocationHasMore] = useState(true);
    const [locationLoading, setLocationLoading] = useState(false);
    const locationSearchTimeout = useRef<NodeJS.Timeout | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [reasonOptions, setReasonOptions] = useState<{ label: string; value: number }[]>([]);
    const [reason, setReason] = useState<{ label: string; value: number } | null>(null);
    const [reasonSearch, setReasonSearch] = useState("");
    const [reasonPage, setReasonPage] = useState(1);
    const [reasonHasMore, setReasonHasMore] = useState(true);
    const [reasonLoading, setReasonLoading] = useState(false);
    const reasonSearchTimeout = useRef<NodeJS.Timeout | null>(null);
    const [showManageReason, setShowManageReason] = useState(false);
    const [editReasonId, setEditReasonId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    const initialItemRow: TableItem = {
        item: null,
        sourceAvailableQuantity: "",
        newQuantity: "",
        quantityAdjusted: "",
        searchTerm: "",
        page: 1,
        hasMore: true,
        options: [],
        loading: false,
    };

    const initialForm: {
        mode: "QUANTITY_BASED_ADJUSTMENT" | "ONHAND_BASED_ADJUSTMENT";
        reference: string;
        date: Date | null;
        description: string;
        location: { label: string; value: string } | null;
        items: TableItem[];
    } = {
        mode: "QUANTITY_BASED_ADJUSTMENT",
        reference: "",
        date: null,
        description: "",
        location: null,
        items: [
            {
                item: null,
                sourceAvailableQuantity: "",
                newQuantity: "",
                quantityAdjusted: "",
                searchTerm: "",
                page: 1,
                hasMore: true,
                options: [],
                loading: false,
            }
        ],
    };

    // --- Load editData or default date ---
    useEffect(() => {
        if (!show) {
            setForm(initialForm);
            setEditId(null);
        }
    }, [show]);

    useEffect(() => {
        if (!show) return;
        setReason(null);
        setEditReasonId(null)

        // CREATE MODE
        if (!editData) {
            setForm({
                ...initialForm,
                date: new Date(),
            });
            setEditId(null);
            return;
        }

        // EDIT MODE: fetch API data
        setEditId(editData.id);
        setLoading(true);
        dispatch(editInventoryAdjustmentAsync({ id: editData.id }))
            .then((res: any) => {
                const data = res?.payload?.data;
                setForm({
                    mode:
                        data?.inventory_mode === "QUANTITY_BASED_ADJUSTMENT"
                            ? "QUANTITY_BASED_ADJUSTMENT"
                            : "ONHAND_BASED_ADJUSTMENT",

                    reference: data?.reference_number || data?.inventory_number,
                    date: data?.transaction_date ? new Date(data?.transaction_date) : new Date(),
                    description: data?.description || "",
                    location: data?.source_warehouse_data
                        ? {
                            label: data?.source_warehouse_data.api_name,
                            value: data?.source_warehouse_data.id,
                        }
                        : null,

                    items:
                        data.inventory_adjustments?.map((item: any) => {
                            // Current Quantity → old_quantity_in_location
                            const currentQty = item.old_quantity_in_location ?? null;
                            // Quantity Adjusted → adjustment_quantity
                            const qtyAdjusted = item.adjustment_quantity ?? null;
                            // New Quantity On Hand → current_quantity_in_location
                            const newQtyOnHand = item.current_quantity_in_location ?? null;

                            return {
                                ...initialItemRow,
                                adjustment_item_id: item.id,
                                item: {
                                    label: item.product_variant?.name || item.sku || "",
                                    value: item.product_variant.id,
                                    sku: item.product_variant?.sku || item.sku || "",
                                    product_id: item?.product_variant?.platform_product?.id ?? item?.product_variant?.platform_product ?? item?.product_variant?.platform_product_id,
                                    adjustment_item_id: item.id,
                                    source_total_quantity: currentQty !== null ? Number(currentQty) : 0,
                                },
                                sourceAvailableQuantity: currentQty !== null ? String(currentQty) : "N/A",
                                newQuantity: newQtyOnHand !== null ? String(newQtyOnHand) : "N/A",
                                quantityAdjusted: qtyAdjusted !== null ? String(qtyAdjusted) : "N/A",
                            };
                        }) || [{ ...initialItemRow }],

                });
                setEditReasonId(data?.reason ?? null);
                setReason(null);
                fetchReasons("", 1);
            }).catch((err) => {
                console.error(err);
                toast.error("Failed to fetch adjustment data");
            })
            .finally(() => setLoading(false));

    }, [show, editData]);

    useEffect(() => {
        if (reason !== null) return; // ✅ user has already selected
        if (!editReasonId || reasonOptions.length === 0) return;

        const matched = reasonOptions.find(
            (opt) => opt.label === editReasonId
        );

        if (matched) {
            setReason(matched);
        }
    }, [editReasonId, reasonOptions, reason]);

    const fetchReasons = async (search = "", page = 1) => {
        if (reasonLoading) return;
        try {
            setReasonLoading(true);

            const res: any = await dispatch(
                getReasonListAsync({
                    paramData: { page, limit: 10, search, isActive: true, },
                }));

            const data = res?.payload?.results?.data?.map((r: any) => ({
                label: r.api_name, value: r.id,
            })) || [];

            setReasonOptions(prev => page === 1 ? data : [...prev, ...data]);
            setReasonPage(page);
            setReasonHasMore(!!res?.payload?.next);
        } catch (err) {
            console.error("Reason fetch error", err);
            toast.error("Failed to fetch reasons");
        } finally {
            setReasonLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (reasonSearchTimeout.current) {
                clearTimeout(reasonSearchTimeout.current);
            }
        };
    }, []);


    // --- Load warehouse options ---
    const fetchLocations = async (search: string, page: number) => {
        try {
            setLocationLoading(true);
            const res: any = await dispatch(
                getWarehouseListAsync({ paramData: { page, limit: 50, search, sort_by: "", warehouse_id: null, isActive: true }, })
            )
            const data = res?.payload?.results?.data?.map((wh: any) => ({
                value: wh.id, label: wh.api_name,
            })) || [];
            setWarehouseOptions(prev => page === 1 ? data : [...prev, ...data]);
            setLocationPage(page);
            setLocationHasMore(res?.payload?.next ? true : false);
            setLocationLoading(false);
        } catch (error: any) {
            console.error("Warehouse fetch error:", error);
            toast.error(error?.data?.message || "Failed to fetch warehouse locations");
        } finally {
            setLocationLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (locationSearchTimeout.current) clearTimeout(locationSearchTimeout.current);
        }
    }, []);

    // --- Fetch items API for a row ---
    const fetchItemOptions = async (rowIndex: number, page = 1, search = "") => {
        const locationId = form.location?.value;
        if (!locationId) return;
        const itemsCopy = [...form.items];
        itemsCopy[rowIndex].loading = true;
        setForm({ ...form, items: itemsCopy });

        const res: any = await dispatch(
            getInventoryVariantsListAsync({
                page, limit: 10, search, sort_by: "asc", order_by: "id",
                source_warehouse_id: String(locationId), destination_warehouse_id: String(locationId),
            })
        );

        const data = res?.payload?.results?.data || [];
        const isNext = !!res?.payload?.next;
        const newOptions = data.map((it: any) => ({
            label: it.name,
            value: it.id,
            sku: it.sku || "",
            product_id: it.platform_product?.id ?? it.platform_product ?? it.platform_product_id ?? 0,
            quantity: it.total_quantity,
            unit: it.unit || "",
            source_total_quantity: it.source_total_quantity || it.total_quantity,
        }));

        itemsCopy[rowIndex].options = page === 1 ? newOptions : [...itemsCopy[rowIndex].options, ...newOptions];
        itemsCopy[rowIndex].page = page;
        itemsCopy[rowIndex].hasMore = isNext;
        itemsCopy[rowIndex].loading = false;
        setForm({ ...form, items: itemsCopy });
    };

    const ReasonMenuList = (
        props: MenuListProps<ReasonOption, false>
    ) => {
        return (
            <>
                <components.MenuList {...props}>
                    {props.children}
                </components.MenuList>

                {/* Manage button INSIDE dropdown */}
                <div
                    onClick={() => setShowManageReason(true)}
                    style={{
                        borderTop: "1px solid #e5e7eb",
                        padding: "10px 12px", cursor: "pointer", display: "flex", alignItems: "center",
                        gap: "8px", fontWeight: 500, color: "#0d6efd", background: "#fff",
                    }}
                >
                    <KTIcon iconName="setting-2" className="fs-6" />
                    Manage Reasons
                </div>
            </>
        );
    };


    const handleValueChange = (
        index: number,
        field: "newQuantity" | "quantityAdjusted",
        value: string
    ) => {
        const updated = [...form.items];
        const available = Number(updated[index].sourceAvailableQuantity || 0);

        if (field === "newQuantity") {
            updated[index].newQuantity = value;
            updated[index].quantityAdjusted =
                value !== ""
                    ? String(Number(value) - available) : "";
        }

        if (field === "quantityAdjusted") {
            updated[index].quantityAdjusted = value;
            updated[index].newQuantity =
                value !== ""
                    ? String(available + Number(value)) : "";
        }
        setForm({ ...form, items: updated });
    };

    const handleAddRow = () => {
        setForm({
            ...form,
            items: [
                ...form.items,
                {
                    item: null,
                    sourceAvailableQuantity: "",
                    newQuantity: "",
                    quantityAdjusted: "",
                    searchTerm: "",
                    page: 1,
                    hasMore: true,
                    options: [],
                    loading: false,
                },
            ],
        });
    };

    const handleRemoveRow = (index: number) => {
        setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
    };

    const handleDeleteConfirmed = useCallback(async () => {
        if (pendingDeleteIndex === null) return;

        const row = form.items[pendingDeleteIndex];
        const adjustmentItemId = row.item?.adjustment_item_id;

        setDeleteLoading(true);

        // Delete via API only if ID present
        if (editData?.id && adjustmentItemId) {
            try {
                await dispatch(
                    deleteItemInventoryAdjustmentAsync({
                        id: editData.id,
                        adjust_itemId: adjustmentItemId,
                    })
                )
                toast.success("Item deleted successfully");
            } catch (err) {
                console.error("delete error:", err);
                toast.error("Failed to delete item");
                setDeleteLoading(false);
                return;
            }
        }

        // Remove from UI
        const updatedRows = form.items.filter((_, i) => i !== pendingDeleteIndex);
        setForm({ ...form, items: updatedRows });

        setPendingDeleteIndex(null);
        setShowDeleteModal(false);
        setDeleteLoading(false);
    }, [dispatch, editData, form, pendingDeleteIndex]);

    const handleSubmit = async (isConverted = false) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);

        const releaseSubmitting = (cooldownMs = 0) => {
            if (cooldownMs > 0) {
                setTimeout(() => {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                }, cooldownMs);
            } else {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            }
        };

        if (!form.reference) {
            toast.error(" Reference Number is required");
            releaseSubmitting(4000);
            return;
        }
        if (!form.date) {
            toast.error("date is required");
            releaseSubmitting(4000);
            return;
        }
        if (!reason) {
            toast.error("Reason is required");
            releaseSubmitting(4000);
            return;
        }
        if (!form.location) {
            toast.error("Warehouse is required");
            releaseSubmitting(4000);
            return;
        }
        if (form.items.some((row) => !row.item)) {
            toast.error("Atleast one Product is required");
            releaseSubmitting(4000);
            return;
        }

        const payload = {
            is_converted: isConverted,
            warehouse_id: form.location.value,
            inventory_mode:
                form.mode === "QUANTITY_BASED_ADJUSTMENT"
                    ? "QUANTITY_BASED_ADJUSTMENT"
                    : "ONHAND_BASED_ADJUSTMENT",
            adjustment_date: form.date ? formatDateForPayload(form.date) : null,
            description: form.description || "",
            reason: reason?.label ?? null,
            reference: form.reference || "",
            items: form.items.map((row) => {
                const baseItem: any = {
                    variant_id: row.item!.value,
                    product_id: row.item!.product_id,
                    sku: row.item!.sku || "",
                    quantity: Number(row.newQuantity || 0),
                };

                const adjustmentItemId =
                    row.item?.adjustment_item_id ?? row.adjustment_item_id;

                if (adjustmentItemId !== undefined) {
                    baseItem.adjustment_item_id = adjustmentItemId;
                }

                return baseItem;
            }),
            custom_fields: [],
        };

        try {
            if (editData) {
                if (editId === null) {
                    return;
                }

                const updateRes: any = await dispatch(
                    updateInventoryAdjustmentAsync({ id: editId, payloadupdate: payload })
                );

                if (updateRes.error) {
                    toast.error(
                        updateRes?.payload?.data?.message ||
                        `Failed to ${editData ? "update" : "save"} adjustment`
                    );
                    return;
                }

                if (isConverted) {
                    const convertRes: any = await dispatch(
                        adjustedInventoryAdjustmentAsync({
                            payloadupdate: {
                                inventory_defination_id: editData.id,
                            },
                        })
                    );

                    if (convertRes.error) {
                        toast.error(
                            convertRes?.payload?.data?.message ||
                            "Adjustment updated but conversion failed"
                        );
                        return;
                    }
                }
            } else {
                const createRes: any = await dispatch(createInventoryAdjustmentAsync(payload));

                if (createRes.error) {
                    toast.error(
                        createRes?.payload?.data?.message ||
                        `Failed to ${editId ? "update" : "create"} adjustment`
                    );
                    return;
                }
            }

            toast.success(
                isConverted
                    ? `${editId ? "Adjustment updated" : "Adjustment created"} & converted successfully!`
                    : `${editId ? "Adjustment updated" : "Adjustment created"} successfully!`
            );

            onSaved(payload);
            setReason(null);
        } catch (err: any) {
            console.error(err);
            toast.error(
                err?.message
                    ? err.message
                    : `Failed to ${editId ? "update" : "create"} adjustment`
            );
        } finally {
            releaseSubmitting(0);
        }
    };

    // NEW Convert function
    const handleSubmitConverted = () => handleSubmit(true);

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
            {/* HEADER */}
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 px-4 py-3" style={{ background: "#f8f9fa", borderBottom: "1px solid #e1e1e1", borderTopLeftRadius: "7px", borderTopRightRadius: "7px" }}>
                <h5 className="m-0 fw-bold">{editData ? !isSynced ? "Edit Adjustment" : "View Adjustment" : "Create Adjustment"}</h5>
                <div className="d-flex gap-2 flex-wrap w-100 w-sm-auto justify-content-start justify-content-sm-end">
                    {/* Cancel Button (Outlined) */}
                    <button
                        className="btn btn-outline-secondary border d-flex align-items-center justify-content-center"
                        style={{
                            padding: "0 calc(8px + 0.4vw)",
                            height: "36px",
                            backgroundColor: "#e6e9ecff",
                            borderColor: "#6c757d",
                            whiteSpace: "nowrap",
                            fontSize: "calc(11px + 0.2vw)"
                        }}
                        onClick={onClose}
                    >
                        Cancel
                    </button>

                    {/* Convert & Adjust (Gray Button) */}
                    {!isSynced && <button
                        className="btn btn-secondary border d-flex align-items-center justify-content-center"
                        style={{
                            padding: "0 calc(8px + 0.4vw)",
                            height: "36px",
                            backgroundColor: "#0d6efd",
                            color: "white",
                            whiteSpace: "nowrap",
                            fontSize: "calc(11px + 0.2vw)"
                        }}
                        onClick={handleSubmitConverted}
                        disabled={isSubmitting}
                    > Save & Adjust Now
                    </button>}

                    {/* Save Button (Primary) */}
                    {!isSynced && <button
                        className="btn btn-primary border d-flex align-items-center justify-content-center"
                        style={{
                            padding: "0 calc(8px + 0.4vw)",
                            height: "36px",
                            whiteSpace: "nowrap",
                            fontSize: "calc(11px + 0.2vw)"
                        }}
                        onClick={() => handleSubmit(false)}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Saving..." : "Save as Draft"}
                    </button>}
                </div>
            </div>

            {/* BODY */}
            <div className="modal-body px-4 py-3" style={{ overflowX: "hidden" }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                        <Loader variant="section" />
                    </div>
                ) : (
                    <>

                        {/* Reference */}
                        <div className="mb-3">
                            <label className="fw-semibold required">Reference Number</label>
                            <input className="form-control mt-1" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                style={{ width: "100%", maxWidth: "30rem", height: "38px" }} disabled={isSynced} />
                        </div>

                        {/* Date */}
                        <div className="mb-3">
                            <label className="fw-semibold">Date</label><br />
                            <DatePicker selected={form.date} onChange={(date) => setForm({ ...form, date })} dateFormat="MM/dd/yyyy" placeholderText="Select date" locale="en-GB"
                                className="form-control" wrapperClassName="custom-datepicker-wrapper" readOnly disabled={true} />
                            <style>
                                {`
                                .custom-datepicker-wrapper {
                                    width: 100% !important;
                                    max-width: 30rem !important;
                                }
                                .custom-datepicker-wrapper .react-datepicker__input-container {
                                    width: 100% !important;
                                    height: 38px !important;
                                }
                                `}
                            </style>
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                            <label className="fw-semibold">Description</label>
                            <textarea className="form-control mt-1" disabled={isSynced} rows={2} maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ width: "100%", maxWidth: "30rem" }} />
                        </div>

                        <div className="mb-4" style={{ width: "100%", maxWidth: "30rem" }}>
                            <label className="fw-semibold required">Reason</label>
                            <Select<ReasonOption, false>
                                value={reason}
                                options={reasonOptions}
                                placeholder="Select reason"
                                isDisabled={isSynced}
                                isClearable
                                isSearchable
                                isLoading={reasonLoading}
                                filterOption={null}
                                inputValue={reasonSearch}
                                components={{ MenuList: ReasonMenuList }}

                                onChange={(val) => setReason(val)}

                                onInputChange={(input, { action }) => {
                                    if (action !== "input-change") return;

                                    setReasonSearch(input);
                                    setReasonPage(1);
                                    setReasonHasMore(true);

                                    if (reasonSearchTimeout.current) {
                                        clearTimeout(reasonSearchTimeout.current);
                                    }

                                    reasonSearchTimeout.current = setTimeout(() => {
                                        fetchReasons(input, 1);
                                    }, 400);
                                }}

                                onMenuOpen={() => {
                                    if (!reasonLoading && reasonPage === 1) {
                                        fetchReasons(reasonSearch || "", 1);
                                    }
                                }}

                                onMenuScrollToBottom={() => {
                                    if (!reasonHasMore || reasonLoading) return;
                                    fetchReasons(reasonSearch, reasonPage + 1);
                                }}
                            />

                        </div>

                        {/* Location */}
                        <div className="mb-4" style={{ width: "100%", maxWidth: "30rem" }}>
                            <label className="fw-semibold required">Warehouse</label>
                            <Select
                                value={form.location}
                                isDisabled={isSynced}
                                onChange={(value, { action }) => {
                                    // Reset all rows
                                    const resetRows = form.items.map((row) => ({
                                        ...row,
                                        item: null,
                                        sourceAvailableQuantity: "",
                                        newQuantity: "",
                                        quantityAdjusted: "",
                                        searchTerm: "",
                                        page: 1,
                                        hasMore: true,
                                        options: [],
                                        loading: false,
                                    }));
                                    if (action === "clear") {
                                        setForm({ ...form, location: null, items: resetRows });
                                        setLocationSearchTerm("");
                                        setLocationPage(1);
                                        setLocationHasMore(true);
                                        setWarehouseOptions([]);
                                        fetchLocations("", 1);
                                        return;
                                    }
                                    // Normal selection
                                    setForm({ ...form, location: value, items: resetRows });
                                    // Reset search state
                                    setLocationSearchTerm("");
                                    setLocationPage(1);
                                    setLocationHasMore(true);
                                }}
                                options={warehouseOptions}
                                placeholder="Select warehouse"
                                isClearable
                                isSearchable
                                filterOption={null}
                                inputValue={locationSearchTerm}
                                isLoading={locationLoading}
                                onInputChange={(inputValue, { action }) => {
                                    if (action !== "input-change") return;
                                    setLocationSearchTerm(inputValue);
                                    setLocationPage(1);
                                    setLocationHasMore(true);
                                    if (locationSearchTimeout.current) clearTimeout(locationSearchTimeout.current);
                                    locationSearchTimeout.current = setTimeout(() => {
                                        fetchLocations(inputValue, 1);
                                    }, 400);
                                }}
                                onMenuScrollToBottom={() => {
                                    if (!locationHasMore || locationLoading) return;
                                    fetchLocations(locationSearchTerm, locationPage + 1);
                                }}
                                onMenuOpen={() => {
                                    // Always fetch first page if input is empty OR options are empty
                                    if (warehouseOptions.length === 0 || locationSearchTerm === "") {
                                        fetchLocations("", 1);
                                    }
                                }}
                            />
                        </div>

                        {/* ITEM TABLE */}
                        <div className="mb-3">
                            <h6 className="fw-bold">Item Table</h6>
                            <div style={{
                                pointerEvents: form.location ? "auto" : "none", opacity: form.location ? 1 : 0.5,
                            }}>
                                <style>
                                    {`
                                    .inventory-item-table-scroll {
                                        width: 100%;
                                        max-width: 100%;
                                        overflow-x: auto;
                                        -webkit-overflow-scrolling: touch;
                                    }
                                    .inventory-item-table-scroll .inventory-item-table {
                                        width: max-content;
                                        min-width: 100%;
                                        margin-bottom: 0;
                                        table-layout: auto;
                                    }
                                    .inventory-item-table .adj-col-item {
                                        min-width: 280px;
                                        width: 280px;
                                    }
                                    .inventory-item-table .adj-col-current {
                                        min-width: 130px;
                                        width: 130px;
                                        text-align: center;
                                        vertical-align: middle;
                                    }
                                    .inventory-item-table .adj-col-new-qty {
                                        min-width: 170px;
                                        width: 170px;
                                    }
                                    .inventory-item-table .adj-col-adjusted {
                                        min-width: 160px;
                                        width: 160px;
                                    }
                                    .inventory-item-table .adj-col-actions {
                                        min-width: 52px;
                                        width: 52px;
                                        text-align: center;
                                        vertical-align: middle;
                                    }
                                    .inventory-item-table th {
                                        white-space: nowrap;
                                        vertical-align: middle;
                                    }
                                    `}
                                </style>
                                <div className="inventory-item-table-scroll">
                                    <table className="table table-bordered inventory-item-table mb-0">
                                        <thead>
                                            <tr className="text-center">
                                                <th className="adj-col-item">Item Details <span className="text-danger">*</span></th>
                                                <th className="adj-col-current">Current Quantity</th>
                                                <th className="adj-col-new-qty">{!isTotalQuantityAdjustment ? "New Quantity On Hand" : "On Hand Adjusted"}</th>
                                                <th className="adj-col-adjusted">Quantity Adjusted <span className="text-danger">*</span></th>
                                                <th className="adj-col-actions" aria-label="Actions" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.items.map((row, index) => {
                                                const isItemMenuOpen = openItemSelectIndex === index;
                                                const itemMenuLayout = isItemMenuOpen
                                                    ? getInventoryItemMenuLayout(itemSelectAnchorRefs.current[index])
                                                    : null;
                                                const getItemAnchor = () => itemSelectAnchorRefs.current[index] ?? null;

                                                return (
                                                <tr key={index}>
                                                    <td className="adj-col-item" data-label="Item Details *">
                                                        <div ref={(el) => { itemSelectAnchorRefs.current[index] = el; }}>
                                                        <InventoryItemSelect<ItemOption>
                                                            value={row.item}
                                                            isDisabled={isSynced}
                                                            onChange={(val, { action }) => {
                                                                const itemsCopy = [...form.items];
                                                                if (action === "clear") {
                                                                    const existingRow = itemsCopy[index];
                                                                    itemsCopy[index] = {
                                                                        ...existingRow,
                                                                        item: null,
                                                                        sourceAvailableQuantity: "",
                                                                        newQuantity: "",
                                                                        quantityAdjusted: "",
                                                                        searchTerm: "",
                                                                        page: 1,
                                                                        hasMore: true,
                                                                        options: [],
                                                                        loading: false,
                                                                     };
                                                                    setForm({ ...form, items: itemsCopy });
                                                                    return;
                                                                }
                                                                const duplicateIndex = form.items.findIndex(
                                                                    (r, i) => i !== index && r.item?.value === val?.value
                                                                );
                                                                if (duplicateIndex !== -1) {
                                                                    toast.error(`Item already selected in Row ${duplicateIndex + 1}`);
                                                                    return;
                                                                }
                                                                const existingRow = itemsCopy[index];
                                                                itemsCopy[index].item = val
                                                                    ? {
                                                                        ...val,
                                                                        ...(existingRow.adjustment_item_id &&
                                                                            existingRow.item?.value === val.value && {
                                                                            adjustment_item_id: existingRow.adjustment_item_id,
                                                                        }),
                                                                    }
                                                                    : null;
                                                                itemsCopy[index].sourceAvailableQuantity = String(val?.source_total_quantity) ?? "";
                                                                itemsCopy[index].newQuantity = "";
                                                                itemsCopy[index].quantityAdjusted = "";
                                                                itemsCopy[index].searchTerm = "";
                                                                itemsCopy[index].options = val ? [val] : [];
                                                                setForm({ ...form, items: itemsCopy });
                                                                    }}
                                                            options={row.options}
                                                            placeholder="Search & Select Item"
                                                            isClearable
                                                            isLoading={row.loading}
                                                            filterOption={null}
                                                            inventoryItemAnchorGetter={getItemAnchor}
                                                            menuIsOpen={isItemMenuOpen}
                                                            maxMenuHeight={itemMenuLayout?.maxMenuHeight}
                                                            menuPlacement={itemMenuLayout?.menuPlacement ?? 'auto'}
                                                            inputValue={row.searchTerm}
                                                            onInputChange={(inputValue, { action }) => {
                                                                if (action === "input-change") {
                                                                    const itemsCopy = [...form.items];
                                                                    itemsCopy[index].searchTerm = inputValue;
                                                                    itemsCopy[index].page = 1;
                                                                    itemsCopy[index].hasMore = true;
                                                                    itemsCopy[index].options = [];
                                                                    setForm({ ...form, items: itemsCopy });
                                                                    if (searchTimers.current[index]) clearTimeout(searchTimers.current[index]);
                                                                    searchTimers.current[index] = setTimeout(() => {
                                                                        fetchItemOptions(index, 1, inputValue);
                                                                    }, 400);
                                                                }
                                                            }}
                                                            onMenuScrollToBottom={() => {
                                                                if (!row.hasMore || row.loading) return;
                                                                fetchItemOptions(index, row.page + 1, row.searchTerm);
                                                            }}
                                                            onMenuOpen={() => {
                                                                setOpenItemSelectIndex(index);
                                                                if (row.options.length === 0) {
                                                                    fetchItemOptions(index, 1, row.searchTerm || "");
                                                                }
                                                            }}
                                                            onMenuClose={() => {
                                                                if (openItemSelectIndex === index) {
                                                                    setOpenItemSelectIndex(null);
                                                                }
                                                            }}
                                                            formatOptionLabel={(option: any) => {
                                                                if (option.sku && !option.label.includes(option.sku)) {
                                                                    return `${option.label} (${option.sku})`;
                                                                }
                                                                return option.label;
                                                            }}
                                                            styles={createInventoryItemSelectStyles(getItemAnchor, {
                                                                container: (provided) => ({
                                                                    ...provided,
                                                                    width: "100%",
                                                                }),
                                                                singleValue: (provided) => ({
                                                                    ...provided,
                                                                    whiteSpace: "normal",
                                                                }),
                                                            })}
                                                        />
                                                        </div>
                                                    </td>
                                                    <td className="adj-col-current" data-label="Current Quantity"><span>{row.sourceAvailableQuantity || ""}</span></td>
                                                    <td className="adj-col-new-qty" data-label={!isTotalQuantityAdjustment ? "New Quantity On Hand" : "On Hand Adjusted"}>
                                                        <input type="number" className="form-control" value={row.newQuantity}
                                                            onChange={(e) => {
                                                                let val = e.target.value;
                                                                if (isTotalQuantityAdjustment && Number(val) < 0) { val = "" }
                                                                handleValueChange(index, "newQuantity", val);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (["e", "E", "+", "-"].includes(e.key)) { e.preventDefault(); }
                                                            }}
                                                            disabled={isSynced}
                                                        />
                                                    </td>

                                                    <td className="adj-col-adjusted" data-label="Quantity Adjusted *">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.quantityAdjusted}
                                                            onChange={(e) =>
                                                                handleValueChange(index, "quantityAdjusted", e.target.value)
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (["e", "E"].includes(e.key)) e.preventDefault();
                                                            }}
                                                            disabled={isSynced}
                                                        />
                                                    </td>

                                                    {/* DELETE BUTTON — kept inside table so it never overflows the modal */}
                                                    <td
                                                        className="adj-col-actions delete-cell"
                                                        data-label=""
                                                    >
                                                        {form.items.length > 1 && !isSynced ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-icon btn-bg-light btn-color-danger btn-sm delete-btn"
                                                                disabled={deleteLoading}
                                                                title="Remove row"
                                                                onClick={() => {
                                                                    const currentRow = form.items[index];

                                                                    if (form.items.length === 1) {
                                                                        toast.error("At least one item is required");
                                                                        return;
                                                                    }

                                                                    if (!currentRow.item?.adjustment_item_id) {
                                                                        const updated = form.items.filter((_, i) => i !== index);
                                                                        setForm({ ...form, items: updated });
                                                                        return;
                                                                    }

                                                                    setPendingDeleteIndex(index);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                            >
                                                                <KTIcon iconName="trash" className="fs-4" />
                                                            </button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {!isSynced && <button className="btn btn-primary d-flex align-items-center justify-content-center mt-3" onClick={handleAddRow} style={{ padding: "0 16px", height: "36px", whiteSpace: "nowrap" }}>+ Add Row</button>}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ManageReasonModal
                show={showManageReason}
                onClose={() => setShowManageReason(false)}
                onSelect={(val) => {
                    setReason(val);
                    setShowManageReason(false);
                    fetchReasons("", 1);
                }}
            />

            <CommonAlertModal
                title="Delete Item?"
                subtitle="This item will be removed from the Adjustment."
                isVisible={showDeleteModal}
                isTermsVisible={false}
                onSuccess={handleDeleteConfirmed}
                onCancel={() => {
                    setPendingDeleteIndex(null);
                    setShowDeleteModal(false);
                }}
                successBtnTitle="Delete"
                cancelBtnTitle="Cancel"
                successBtnClasses="btn btn-sm btn-danger m-2"
            />
        </Modal>
    );
};

export default CreateEditAdjustmentModal;
